import React, { lazy } from 'react';
import {
  unstable_HistoryRouter as HistoryRouter, Routes, Route, Navigate
} from 'react-router-dom';
import customHistory from './customHistory';
import ChatbotOptions from './components/chatbots/Options';
import ErrorBoundary from './ErrorBoundary';
import lazyWithRetry from './utils/lazyWithRetry';
import ProtectedRoute from './ProtectedRoute';
import ChatbotAIRoutes from './ChatbotAIRoutes';
import { CRM_ROUTES } from './constants/RoutesConstants';
import SidebarContainerMC from './presentation/components/SidebarContainerMC';
import RemindersScreenMC from './presentation/screens/retailers/slug/RemindersScreenMC';
import WebhooksScreenMC from './presentation/screens/retailers/slug/WebhooksScreenMC';
import WhatsappFlowsScreenMC from './presentation/screens/retailers/slug/WhatsappFlowsScreenMC';
import WhatsappFlowResponsesScreenMC from './presentation/screens/retailers/slug/WhatsappFlowResponsesScreenMC';
import ModuleTitleMC from './presentation/layouts/ModuleTitleMC';
import ProductSwitcherMC from './presentation/components/ProductSwitcherMC';
import MarketingSidebarSyncMC from './presentation/components/MarketingSidebarSyncMC';
import TikTokNavBadgeSyncMC from './presentation/components/TikTokNavBadgeSyncMC';
import WhatsappCallsNavSyncMC from './presentation/components/WhatsappCallsNavSyncMC';
// Pantallas de error
import TestErrorScreenMC from './presentation/screens/TestErrorScreenMC';
// Super Admin
const SuperAdminDashboardScreenMC = lazy(() => import('./presentation/screens/retailers/slug/superAdmin/SuperAdminDashboardScreenMC'));
const SuperAdminRetailerListScreenMC = lazy(() => import('./presentation/screens/retailers/slug/superAdmin/SuperAdminRetailerListScreenMC'));
const SuperAdminFinancialDataScreenMC = lazy(() => import('./presentation/screens/retailers/slug/superAdmin/SuperAdminFinancialDataScreenMC'));
const SuperAdminSupportScreenMC = lazy(() => import('./presentation/screens/retailers/slug/superAdmin/SuperAdminSupportScreenMC'));
import ErrorPageScreenMC from './presentation/screens/ErrorPageScreenMC';

const ConversationTopics = lazy(() => import('./components/ConversationTopics'));
const ContactGroup = lazy(() => import('./components/ContactGroup'));
const Funnels = lazy(() => import('./components/Funnels'));
const FunnelAutomations = lazy(() => import('./components/Funnels/Automations'));
const DownloadHistory = lazy(() => import('./presentation/screens/Funnels/DownloadHistory'));
const WsTemplatesNew = lazy(() => import('./components/WsTemplates/WsTemplatesNew'));
const MessagingStats = lazy(() => import('./components/Stats/Messaging'));
const ResponseTimeStats = lazy(() => import('./components/Stats/Messaging/ResponseTimeStatsWrapper'));
const MessageVolumeStats = lazy(() => import('./components/Stats/Messaging/MessageVolumeStatsWrapper'));
const AgentPerformanceStats = lazy(() => import('./components/Stats/Messaging/AgentPerformanceStatsWrapper'));
const ClientProfileStats = lazy(() => import('./components/Stats/Messaging/ClientProfileStatsWrapper'));
const AttributionStats = lazy(() => import('./presentation/screens/retailers/slug/attributionStats'));
const AttributionByChannelStats = lazy(() => import('./presentation/screens/retailers/slug/attributionStats/ByChannelScreen'));
const AttributionFunnelStats = lazy(() => import('./presentation/screens/retailers/slug/attributionStats/FunnelScreen'));
const SalesStats = lazy(() => import('./components/Stats/Sales'));
const SalesDashboard = lazy(() => import('./components/Stats/Sales/SalesDashboardWrapper'));
const ResumenStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.ResumenStatsWrapper })));
const WhatISellStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.WhatISellStatsWrapper })));
const PorDondeStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.PorDondeStatsWrapper })));
const EquipoClientesStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.EquipoClientesStatsWrapper })));
const ConversionEficienciaStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.ConversionEficienciaStatsWrapper })));
const MarketingStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.MarketingStatsWrapper })));
const EffectivePurchasesStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.EffectivePurchasesStatsWrapper })));
const AverageTicketStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.AverageTicketStatsWrapper })));
const ConversionRateStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.ConversionRateStatsWrapper })));
const SalesGrowthStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.SalesGrowthStatsWrapper })));
const SalesByChannelStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.SalesByChannelStatsWrapper })));
const SalesByDeliveryStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.SalesByDeliveryStatsWrapper })));
const SalesByAgentStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.SalesByAgentStatsWrapper })));
const AgentEfficiencyStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.AgentEfficiencyStatsWrapper })));
const TopCustomersStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.TopCustomersStatsWrapper })));
const SalesByTagStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.SalesByTagStatsWrapper })));
const CustomersConversionStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.CustomersConversionStatsWrapper })));
const TimeToFirstPurchaseStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.TimeToFirstPurchaseStatsWrapper })));
const SalesPerMessageStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.SalesPerMessageStatsWrapper })));
const SalesByCampaignStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.SalesByCampaignStatsWrapper })));
const BrandsStatsWrapper = lazy(() => import('./components/Stats/Sales/SalesStatsWrappers').then(m => ({ default: m.BrandsStatsWrapper })));
const TagsStats = lazy(() => import('./components/Stats/Tags'));
const FunnelsDashboard = lazy(() => import('./components/Stats/Funnels/FunnelsDashboardWrapper'));
const PipelineStats = lazy(() => import('./components/Stats/Funnels/PipelineStatsWrapper'));
const DealsStats = lazy(() => import('./components/Stats/Funnels/DealsStatsWrapper'));
const TimeInStageStats = lazy(() => import('./components/Stats/Funnels/TimeInStageStatsWrapper'));
const ConversionRateStats = lazy(() => import('./components/Stats/Funnels/ConversionRateStatsWrapper'));
const LossRateStats = lazy(() => import('./components/Stats/Funnels/LossRateStatsWrapper'));
const VelocityStats = lazy(() => import('./components/Stats/Funnels/VelocityStatsWrapper'));
const StagnationRateStats = lazy(() => import('./components/Stats/Funnels/StagnationRateStatsWrapper'));
const DeathStageStats = lazy(() => import('./components/Stats/Funnels/DeathStageStatsWrapper'));
const LossReasonsStats = lazy(() => import('./components/Stats/Funnels/LossReasonsStatsWrapper'));
const WinProbabilityStats = lazy(() => import('./components/Stats/Funnels/WinProbabilityStatsWrapper'));
const ConversationTopicStatsPage = lazy(() => import('./components/Stats/ConversationTopic'));
const AiAgentStats = lazy(() => import('./presentation/screens/retailers/slug/aiAgentsStats'));
const AiAgentsPerformance = lazy(() => import('./presentation/screens/retailers/slug/aiAgentsStats/PerformanceScreen'));
const AiAgentsFinancialImpact = lazy(() => import('./presentation/screens/retailers/slug/aiAgentsStats/FinancialImpactScreen'));
const AiAgentsPurchaseIntent = lazy(() => import('./presentation/screens/retailers/slug/aiAgentsStats/PurchaseIntentScreen'));
const AiAgentsHandoffs = lazy(() => import('./presentation/screens/retailers/slug/aiAgentsStats/HandoffsScreen'));
const AutomaticAnswers = lazy(() => import('./components/AutomaticAnswers'));
const ProductsMC = lazy(() => lazyWithRetry(
  () => import('./presentation/screens/retailers/slug/products/ListScreenMC'),
  'ProductsMC'
));
const Inventories = lazy(() => import('./components/ProductVariantCombinations'));
const ImportProducts = lazy(() => import('./components/Products/ImportProducts'));
const Orders = lazy(() => lazyWithRetry(
  () => import('./presentation/modules/Orders/pages/OrdersListPage'),
  'Orders'
));
const ShowOrder = lazy(() => import('./presentation/modules/Orders/pages/OrderShowPage'));
const EditOrder = lazy(() => import('./components/Orders/EditOrder'));
const Categories = lazy(() => lazyWithRetry(
  () => import('./presentation/screens/retailers/slug/categories/CategoriesScreenMC'),
  'Categories'
));
const NewOrder = lazy(() => import('./components/Orders/NewOrder'));
const OrderDownloadHistory = lazy(() => lazyWithRetry(
  () => import('./presentation/modules/Orders/pages/OrdersDownloadHistoryPage'),
  'OrderDownloadHistory'
));
const CustomerList = lazy(() => import('./components/CustomerList'));
const CreateCustomerListModuleMC = lazy(() => import('./presentation/modules/CustomerList/CreateCustomerListModuleMC'));
const DigitalPayments = lazy(() => import('./presentation/modules/DigitalPayments'));
const Chatbots = lazy(() => import('./components/chatbots'));
const CustomFields = lazy(() => import('./presentation/modules/CustomFields'));
const Messaging = lazy(() => lazyWithRetry(
  () => import('./components/Messaging'),
  'Messaging'
));
const Staff = lazy(() => import('./components/Staff'));
const SalesChannelsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/sales-channels/SalesChannelsScreenMC'));
const BrandsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/brands/BrandsScreenMC'));
const ImportCustomers = lazy(() => import('./components/Customers/ImportCustomers'));
const ShopifyAutomationsModuleMC = lazy(() => import('./presentation/modules/Shopify/ShopifyAutomationsModuleMC'));
const ShopifyHistoryModuleMC = lazy(() => import('./presentation/modules/Shopify/ShopifyHistoryModuleMC'));
const ShopifyConfigModuleMC = lazy(() => import('./presentation/modules/Shopify/ShopifyConfigModuleMC'));
const ShopifyWhatsAppWidgetModuleMC = lazy(() => import('./presentation/modules/Shopify/ShopifyWhatsAppWidgetModuleMC'));
const ManageProduct = lazy(() => import('./presentation/screens/retailers/slug/products/ManageProductScreenMC'));
const Teams = lazy(() => import('./components/Teams'));
const Roles = lazy(() => import('./components/Roles'));
const QuickAnswers = lazy(() => import('./components/QuickAnswers'));
const TeamAssignments = lazy(() => import('./components/TeamAssignments'));
const AssignmentLogs = lazy(() => import('./components/TeamAssignments/AssignmentLogs'));
const ManageQuickAnswer = lazy(() => import("./components/QuickAnswers/ManageQuickAnswer"));
const ExportedCustomers = lazy(() => import('./presentation/screens/retailers/slug/ExportedCustomers'));
const GrowthToolAutomation = lazy(() => import('./components/GrowthTools/Automations'));
const GrowthToolAutomationHistory = lazy(() => import('./components/GrowthTools/Automations/History'));
const GrowthToolFunnelHistory = lazy(() => import('./components/GrowthTools/Automations/Funnels/History'));
const GrowthToolAutomationFunnels = lazy(() => import('./components/GrowthTools/Automations/Funnels'));
const Notifications = lazy(() => import('./components/Settings/Notifications'));
const Plans = lazy(() => import('./presentation/screens/retailers/slug/plans/PlansScreen'));
const PaymentPlanDetails = lazy(() => import('./components/Plans/PaymentPlanDetails'));
const RegionalConfigsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/RegionalConfigsScreenMC'));
const CustomerFromRb = lazy(() => import('./components/shared/CustomerFromRb'));
const ManageCustomer = lazy(() => import('./components/Customers/ManageCustomer'));
const ShippingCostLayout = lazy(() => import('./components/ShippingCost'));
const ShippingCost = lazy(() => import('./components/ShippingCost/ShippingCost'));
const FixedCost = lazy(() => import('./components/ShippingCost/FixedCost'));
const CostsByLocation = lazy(() => import('./components/ShippingCost/CostsByLocation'));
const EditVariantsPage = lazy(() => import('./presentation/modules/Products/EditVariantsPage'));
const WhatsAppBusinessMC = lazy(() => import('./presentation/modules/Integrations/whatsapp/WhatsAppBusinessMC'));
const OnboardingApp = lazy(() => import('./components/onboarding/OnboardingApp'));
const WhatsAppWidget = lazy(() => import('./presentation/screens/retailers/slug/whatsapp_widget/WhatsappWidgetScreenMC'));
const CampaignsMC = lazy(() => lazyWithRetry(
  () => import('./presentation/screens/retailers/slug/campaigns/CampaignsScreenMC'),
  'CampaignsScreenMC'
));
const CampaignsStatusScreenMC = lazy(() => import('./presentation/screens/retailers/slug/campaigns/slug/CampaignsStatusScreenMC'));
const ShowCustomersScreenMC = lazy(() => import('./presentation/screens/retailers/slug/customers/ShowCustomerScreen/ShowCustomersScreenMC'));
const TagMC = lazy(() => import('./presentation/screens/retailers/slug/TagsScreenMC'));
const IntegrationsScreenMC = lazy(() => lazyWithRetry(
  () => import('./presentation/screens/retailers/slug/integrations/IntegrationsScreenMC'),
  'IntegrationsScreenMC'
));
const HubspotScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/HubspotScreenMC'));
const MetaAdsIntegrationPage = lazy(() => import('./presentation/modules/Integrations/MetaAdsIntegrationPage'));
const GoogleAdsIntegrationPage = lazy(() => import('./presentation/modules/Integrations/GoogleAdsIntegrationPage'));
const NequiMC = lazy(() => import('./presentation/modules/Integrations/wava/NequiMC'));
const DaviplataMC = lazy(() => import('./presentation/modules/Integrations/wava/DaviplataMC'));
const CustomerRelatedFieldsMC = lazy(() => import('./presentation/screens/retailers/slug/CustomerRelatedFieldsScreenMC'));
const HubspotSettingsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/HubspotSettingsScreenMC'));
const RegistrationsEditProfileScreenMC = lazy(() => import('./presentation/screens/retailers/slug/RegistrationsEditProfileScreenMC'));
const MyAccountScreenMC = lazy(() => import('./presentation/screens/retailers/slug/my_account/MyAccountScreenMC'));
const CustomersScreenMC = lazy(() => lazyWithRetry(
  () => import('./presentation/screens/retailers/slug/customers/CustomersScreenMC/CustomersScreenMC'),
  'CustomersScreenMC'
));
const WoocommerceScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/WoocommerceScreenMC'));
const ContificoScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/ContificoScreenMC'));
const ContificoSettingsModuleMC = lazy(() => import('./presentation/modules/Integrations/contifico/ContificoSettingsModuleMC'));
const PaymentGatewayConfigScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/PaymentGatewayConfigScreenMC'));
const SocialIntegrationDetail = lazy(() => import('./presentation/modules/Integrations/social/SocialIntegrationDetail'));
const SocialIntegrationConnect = lazy(() => import('./presentation/modules/Integrations/social/SocialIntegrationConnect'));
const VtexScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/VtexScreenMC'));
const VtexSettingsModuleMC = lazy(() => import('./presentation/modules/Integrations/vtex/VtexSettingsModuleMC'));
const VtexAutomationsModuleMC = lazy(() => import('./presentation/modules/Integrations/vtex/VtexAutomationsModuleMC'));
const VtexHistoryModuleMC = lazy(() => import('./presentation/modules/Integrations/vtex/VtexHistoryModuleMC'));
const TikTokMessagesScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/TikTokMessagesScreenMC'));
const TikTokCommentsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/TikTokCommentsScreenMC'));
const MessengerScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/MessengerScreenMC'));
const WhatsAppScreenMC = lazy(() => import('./presentation/screens/retailers/slug/integrations/WhatsAppScreenMC'));
const MiaConversationsAttendedMC = lazy(() => import('./presentation/screens/retailers/slug/MiaConversationsAttendedMC'));
const CampaignFeeConsumptionsMC = lazy(() => import('./presentation/screens/retailers/slug/CampaignFeeConsumptionsMC'));
const WsTemplatesScreenMC = lazy(() => import('./presentation/screens/retailers/slug/messagingConfiguration/WsTemplatesScreenMC'));
const MessagingRulesScreenMC = lazy(() => import('./presentation/screens/retailers/slug/messagingConfiguration/MessagingRulesScreenMC'));
const LocationsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/locations/LocationsScreenMC'));
const EditBusinessInfoScreenMC = lazy(() => import('./presentation/screens/retailers/slug/edit_business_info/EditBusinessInfoScreenMC'));
const DomainScreenMC = lazy(() => import('./presentation/screens/retailers/slug/domain/DomainScreenMC'));
const SchedulesScreenMC = lazy(() => import('./presentation/screens/retailers/slug/schedules/SchedulesScreenMC'));
const PaymentPlansScreenMC = lazy(() => import('./presentation/screens/retailers/slug/payment_plans/PaymentPlansScreenMC'));
const ConsumptionScreenMC = lazy(() => import('./presentation/screens/retailers/slug/consumption/ConsumptionScreenMC'));
const BillingHistoryScreenMC = lazy(() => import('./presentation/screens/retailers/slug/billing_history/BillingHistoryScreenMC'));
const InvoiceScreenMC = lazy(() => import('./presentation/screens/retailers/slug/invoice/InvoiceScreenMC'));
const ContactsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/contacts/ContactsScreenMC'));
const CalendarEventsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/calendar_events/CalendarEventsScreenMC'));
const TemplatesListModuleMC = lazy(() => import('./presentation/modules/Quotes/TemplatesListModuleMC'));
const TemplateEditorModuleMC = lazy(() => import('./presentation/modules/Quotes/TemplateEditorModuleMC'));
const QuoteDraftsListModuleMC = lazy(() => import('./presentation/modules/Quotes/QuoteDraftsListModuleMC'));
const QuoteEditorModuleMC = lazy(() => import('./presentation/modules/Quotes/QuoteEditorModuleMC'));
const BusinessConfigScreenMC = lazy(() => import('./presentation/screens/retailers/slug/business_config/BusinessConfigScreenMC'));
const ShippingScreenMC = lazy(() => import('./presentation/screens/retailers/slug/shipping/ShippingScreenMC'));
const DashboardScreenMC = lazy(() => lazyWithRetry(
  () => import('./presentation/screens/retailers/slug/DashboardScreenMC'),
  'DashboardScreenMC'
));
const AcademyScreenMC = lazy(() => import('./presentation/modules/academy/AcademyScreenMC'));
const AdsCenterScreenMC = lazy(() => import('./presentation/screens/retailers/slug/AdsCenter/AdsCenterScreenMC'));
const MarketingScreenMC = lazy(() => import('./presentation/screens/retailers/slug/marketing/MarketingScreenMC'));
const LoginScreenMC = lazy(() => import('./presentation/screens/auth/login/LoginScreenMC'));
const SignupScreenMC = lazy(() => import('./presentation/screens/auth/signup/SignupScreenMC'));
const ForgotPasswordScreenMC = lazy(() => import('./presentation/screens/auth/forgot-password/ForgotPasswordScreenMC'));
const ResetPasswordScreenMC = lazy(() => import('./presentation/screens/auth/reset-password/ResetPasswordScreenMC'));
const OtpVerificationScreenMC = lazy(() => import('./presentation/screens/auth/otp-verification/OtpVerificationScreenMC'));
const OtpSetupScreenMC = lazy(() => import('./presentation/screens/auth/otp-setup/OtpSetupScreenMC'));
const AcceptInvitationScreenMC = lazy(() => import('./presentation/screens/auth/accept-invitation/AcceptInvitationScreenMC'));
const TwoFactorModuleMC = lazy(() => import('./presentation/modules/Security/TwoFactorModuleMC'));
const GetYourPlanScreen = lazy(() => import('./presentation/screens/public/GetYourPlan'));
const HistoryCallScreenMC = lazy(() => import('./presentation/screens/retailers/slug/fonema/HistoryCallScreen'));
const CallsIntegrationScreenMC = lazy(() => import('./presentation/modules/calls/CallsIntegrationModuleMC'));
const InboxScreenMC = lazy(() => import('./presentation/screens/retailers/slug/inbox/InboxScreenMC'));
const ApiKeyScreenMC = lazy(() => import('./presentation/screens/retailers/slug/api_key/ApiKeyScreenMC'));
const VoiceLiveCallsScreenMC = lazy(() => import('./presentation/screens/retailers/slug/voice/VoiceLiveCallsScreenMC'));

const STATS_ROUTES = [
  { path: '/retailers/:slug/new_stats', element: MessagingStats },
  { path: '/retailers/:slug/new_stats/response_times', element: ResponseTimeStats },
  { path: '/retailers/:slug/new_stats/message_volume', element: MessageVolumeStats },
  { path: '/retailers/:slug/new_stats/agent_performance', element: AgentPerformanceStats },
  { path: '/retailers/:slug/new_stats/client_profile', element: ClientProfileStats },
  { path: '/retailers/:slug/attribution_stats/channels', element: AttributionByChannelStats },
  { path: '/retailers/:slug/attribution_stats/funnel', element: AttributionFunnelStats },
  { path: '/retailers/:slug/attribution_stats', element: AttributionStats },
  { path: '/retailers/:slug/sales_stats', element: SalesDashboard },
  { path: '/retailers/:slug/sales_stats/resumen', element: ResumenStatsWrapper },
  { path: '/retailers/:slug/sales_stats/what-i-sell', element: WhatISellStatsWrapper },
  { path: '/retailers/:slug/sales_stats/where-i-sell', element: PorDondeStatsWrapper },
  { path: '/retailers/:slug/sales_stats/equipo-clientes', element: EquipoClientesStatsWrapper },
  { path: '/retailers/:slug/sales_stats/conversion-eficiencia', element: ConversionEficienciaStatsWrapper },
  { path: '/retailers/:slug/sales_stats/marketing', element: MarketingStatsWrapper },
  { path: '/retailers/:slug/sales_stats/purchases', element: EffectivePurchasesStatsWrapper },
  { path: '/retailers/:slug/sales_stats/average-ticket', element: AverageTicketStatsWrapper },
  { path: '/retailers/:slug/sales_stats/conversion', element: ConversionRateStatsWrapper },
  { path: '/retailers/:slug/sales_stats/growth', element: SalesGrowthStatsWrapper },
  { path: '/retailers/:slug/sales_stats/by-channel', element: SalesByChannelStatsWrapper },
  { path: '/retailers/:slug/sales_stats/by-delivery', element: SalesByDeliveryStatsWrapper },
  { path: '/retailers/:slug/sales_stats/by-agent', element: SalesByAgentStatsWrapper },
  { path: '/retailers/:slug/sales_stats/agent-efficiency', element: AgentEfficiencyStatsWrapper },
  { path: '/retailers/:slug/sales_stats/top-customers', element: TopCustomersStatsWrapper },
  { path: '/retailers/:slug/sales_stats/by-tag', element: SalesByTagStatsWrapper },
  { path: '/retailers/:slug/sales_stats/customers-conversion', element: CustomersConversionStatsWrapper },
  { path: '/retailers/:slug/sales_stats/time-to-purchase', element: TimeToFirstPurchaseStatsWrapper },
  { path: '/retailers/:slug/sales_stats/sales-per-message', element: SalesPerMessageStatsWrapper },
  { path: '/retailers/:slug/sales_stats/by-campaign', element: SalesByCampaignStatsWrapper },
  { path: '/retailers/:slug/tags_stats', element: TagsStats },
  { path: '/retailers/:slug/funnels_dashboard', element: FunnelsDashboard },
  { path: '/retailers/:slug/funnels_dashboard/pipeline', element: PipelineStats },
  { path: '/retailers/:slug/funnels_dashboard/deals', element: DealsStats },
  { path: '/retailers/:slug/funnels_dashboard/time', element: TimeInStageStats },
  { path: '/retailers/:slug/funnels_dashboard/conversion', element: ConversionRateStats },
  { path: '/retailers/:slug/funnels_dashboard/loss', element: LossRateStats },
  { path: '/retailers/:slug/funnels_dashboard/velocity', element: VelocityStats },
  { path: '/retailers/:slug/funnels_dashboard/stagnation', element: StagnationRateStats },
  { path: '/retailers/:slug/funnels_dashboard/death-stage', element: DeathStageStats },
  { path: '/retailers/:slug/funnels_dashboard/loss-reasons', element: LossReasonsStats },
  { path: '/retailers/:slug/funnels_dashboard/win-probability', element: WinProbabilityStats },
  { path: '/retailers/:slug/conversation_stats', element: ConversationTopicStatsPage },
];

const AppRoutes = () => (
  <HistoryRouter history={customHistory}>
    <ErrorBoundary>
      <Routes>
        <Route
          exact
          path="/sign_in"
          element={<LoginScreenMC />}
        />
        <Route
          exact
          path="/login"
          element={<LoginScreenMC />}
        />
        <Route
          exact
          path="/register"
          element={<SignupScreenMC />}
        />
        <Route
          exact
          path="/password/new"
          element={<ForgotPasswordScreenMC />}
        />
        <Route
          exact
          path="/password/edit"
          element={<ResetPasswordScreenMC />}
        />
        <Route
          exact
          path="/otp_verification"
          element={<OtpVerificationScreenMC />}
        />
        <Route
          exact
          path="/otp_setup"
          element={<OtpSetupScreenMC />}
        />
        <Route
          exact
          path="/invitation/accept"
          element={<AcceptInvitationScreenMC />}
        />
        <Route exact path="/retailers/:slug/dashboard" element={<ProtectedRoute element={DashboardScreenMC} />} />
        <Route exact path="/retailers/:slug/security/2fa" element={<ProtectedRoute element={TwoFactorModuleMC} />} />
        <Route exact path="/retailers/:slug/api_key_settings" element={<ProtectedRoute element={ApiKeyScreenMC} />} />
        <Route exact path="/retailers/:slug/inbox" element={<ProtectedRoute element={InboxScreenMC} />} />
        <Route
          exact
          path="/retailers/:slug/customer_related_fields"
          element={<CustomerRelatedFieldsMC />}
        />
        <Route exact path="/retailers/:slug/hubspot" element={<ProtectedRoute element={HubspotSettingsScreenMC} />} />
        <Route
          exact
          path="/retailers/:slug/edit"
          element={<RegistrationsEditProfileScreenMC />}
        />
        <Route path="/retailers/:slug/customers/:id/edit" element={<ProtectedRoute element={ManageCustomer} />} />
        <Route exact path="/retailers/:slug/customers/:id" element={<ProtectedRoute element={ShowCustomersScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations" element={<ProtectedRoute element={IntegrationsScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/hubspot_page" element={<ProtectedRoute element={HubspotScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/meta_ads" element={<ProtectedRoute element={MetaAdsIntegrationPage} />} />
        <Route exact path="/retailers/:slug/integrations/google_ads" element={<ProtectedRoute element={GoogleAdsIntegrationPage} />} />
        <Route exact path="/retailers/:slug/integrations/nequi" element={<ProtectedRoute element={NequiMC} />} />
        <Route exact path="/retailers/:slug/integrations/daviplata" element={<ProtectedRoute element={DaviplataMC} />} />
        <Route exact path="/retailers/:slug/integrations/contifico" element={<ProtectedRoute element={ContificoScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/contifico/settings" element={<ProtectedRoute element={ContificoSettingsModuleMC} />} />
        <Route exact path="/retailers/:slug/integrations/messenger" element={<ProtectedRoute element={SocialIntegrationDetail} platform="messenger" />} />
        <Route exact path="/retailers/:slug/integrations/messenger_connect" element={<ProtectedRoute element={SocialIntegrationConnect} platform="messenger" />} />
        <Route exact path="/retailers/:slug/integrations/instagram" element={<ProtectedRoute element={SocialIntegrationDetail} platform="instagram" />} />
        <Route exact path="/retailers/:slug/integrations/instagram_connect" element={<ProtectedRoute element={SocialIntegrationConnect} platform="instagram" />} />
        <Route exact path="/retailers/:slug/integrations/facebook_comments" element={<ProtectedRoute element={SocialIntegrationDetail} platform="facebook_comments" />} />
        <Route exact path="/retailers/:slug/integrations/facebook_comments_connect" element={<ProtectedRoute element={SocialIntegrationConnect} platform="facebook_comments" />} />
        <Route exact path="/retailers/:slug/integrations/instagram_comments" element={<ProtectedRoute element={SocialIntegrationDetail} platform="instagram_comments" />} />
        <Route exact path="/retailers/:slug/integrations/instagram_comments_connect" element={<ProtectedRoute element={SocialIntegrationConnect} platform="instagram_comments" />} />
        <Route exact path="/retailers/:slug/integrations/shopify" element={<ProtectedRoute element={PaymentGatewayConfigScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/mercado_pago" element={<ProtectedRoute element={PaymentGatewayConfigScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/stripe" element={<ProtectedRoute element={PaymentGatewayConfigScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/payphone" element={<ProtectedRoute element={PaymentGatewayConfigScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/vtex" element={<ProtectedRoute element={VtexScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/vtex/settings" element={<ProtectedRoute element={VtexSettingsModuleMC} />} />
        <Route exact path="/retailers/:slug/integrations/vtex/automations" element={<ProtectedRoute element={VtexAutomationsModuleMC} />} />
        <Route exact path="/retailers/:slug/integrations/vtex/automations/:webId/history" element={<ProtectedRoute element={VtexHistoryModuleMC} />} />
        <Route
          exact
          path="/retailers/:slug/contact_groups/new"
          element={<ProtectedRoute element={ContactGroup} />}
        />
        <Route
          exact
          path="/retailers/:slug/contact_groups/:id/edit"
          element={<ProtectedRoute element={ContactGroup} />}
        />
        <Route
          exact
          path="/retailers/:slug/funnels"
          element={<ProtectedRoute element={Funnels} />}
        />
        <Route
          exact
          path="/retailers/:slug/funnels/:funnelId"
          element={<ProtectedRoute element={Funnels} />}
        />
        <Route exact path="/retailers/:slug/funnels/download-history" element={<ProtectedRoute element={DownloadHistory} />} />
        <Route exact path="/retailers/:slug/funnels/automations" element={<ProtectedRoute element={FunnelAutomations} />} />
        <Route
          exact
          path="/retailers/:slug/products"
          element={<ProtectedRoute element={ProductsMC} />}
        />
        <Route
          path="/retailers/:slug/products/new"
          element={<ProtectedRoute element={ManageProduct} />}
        />
        <Route
          path="/retailers/:slug/products/:id/edit"
          element={<ProtectedRoute element={ManageProduct} />}
        />
        <Route
          exact
          path="/retailers/:slug/products/import"
          element={<ProtectedRoute element={ImportProducts} />}
        />
        <Route exact path="/retailers/:slug/inventories" element={<ProtectedRoute element={Inventories} />} />
        <Route path="/retailers/:slug/inventories/:id/edit" element={<ProtectedRoute element={EditVariantsPage} />} />
        <Route
          exact
          path="/retailers/:slug/orders"
          element={<ProtectedRoute element={Orders} />}
        />
        <Route
          exact
          path="/retailers/:slug/custom_fields"
          element={<ProtectedRoute element={CustomFields} />}
        />
        <Route
          exact
          path="/retailers/:slug/orders/new"
          element={<ProtectedRoute element={NewOrder} />}
        />
        <Route
          exact
          path="/retailers/:slug/orders/:id"
          element={<ProtectedRoute element={ShowOrder} />}
        />
        <Route
          exact
          path="/retailers/:slug/orders/:id/edit"
          element={<ProtectedRoute element={EditOrder} />}
        />
        <Route exact path="/retailers/:slug/orders_download_history" element={<ProtectedRoute element={OrderDownloadHistory} />} />
        <Route
          exact
          path="/retailers/:slug/categories"
          element={<ProtectedRoute element={Categories} />}
        />
        <Route
          exact
          path="/retailers/:slug/whatsapp_templates"
          element={<ProtectedRoute element={WsTemplatesScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/whatsapp_templates/new"
          element={<ProtectedRoute element={WsTemplatesNew} />}
        />
        {STATS_ROUTES.map(({ path, element }) => (
          <Route
            key={path}
            exact
            path={path}
            element={<ProtectedRoute element={element} requiredPermission="view_statistics" />}
          />
        ))}
        <Route
          exact
          path="/retailers/:slug/ai_agents_stats"
          element={<ProtectedRoute element={AiAgentStats} />}
        />
        <Route
          exact
          path="/retailers/:slug/ai_agents_stats/performance"
          element={<ProtectedRoute element={AiAgentsPerformance} />}
        />
        <Route
          exact
          path="/retailers/:slug/ai_agents_stats/financial-impact"
          element={<ProtectedRoute element={AiAgentsFinancialImpact} />}
        />
        <Route
          exact
          path="/retailers/:slug/ai_agents_stats/purchase-intent"
          element={<ProtectedRoute element={AiAgentsPurchaseIntent} />}
        />
        <Route
          exact
          path="/retailers/:slug/ai_agents_stats/handoffs"
          element={<ProtectedRoute element={AiAgentsHandoffs} />}
        />
        <Route
          exact
          path="/retailers/:slug/new_manage_automatic_answers"
          element={<ProtectedRoute element={AutomaticAnswers} />}
        />
        <Route exact path="/retailers/:slug/customers" element={<ProtectedRoute element={CustomersScreenMC} />} />
        <Route exact path="/retailers/:slug/exported_customers" element={<ProtectedRoute element={ExportedCustomers} />} />
        <Route path="/retailers/:slug/customers/new" element={<ProtectedRoute element={ManageCustomer} />} />
        <Route path="/retailers/:slug/customers/:id/edit" element={<ProtectedRoute element={ManageCustomer} />} />
        <Route
          exact
          path="/retailers/:slug/customer_list"
          element={<ProtectedRoute element={CustomerList} />}
        />
        <Route
          exact
          path="/retailers/:slug/customer_list/add"
          element={<ProtectedRoute element={CreateCustomerListModuleMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/customer_list/import"
          element={<ProtectedRoute element={ImportCustomers} />}
        />
        <Route
          exact
          path="/retailers/:slug/digital_payments"
          element={<ProtectedRoute element={DigitalPayments} />}
        />
        <Route
          exact
          path="/retailers/:slug/chatbots"
          element={<ProtectedRoute element={Chatbots} />}
        />
        <Route
          exact
          path="/retailers/:slug/chatbots/:id/options"
          element={<ProtectedRoute element={ChatbotOptions} />}
        />
        <Route
          exact
          path="/retailers/:slug/messaging"
          element={<ProtectedRoute element={Messaging} />}
        />
        <Route
          exact
          path="/retailers/:slug/staff"
          element={<ProtectedRoute element={Staff} />}
        />
        <Route
          exact
          path="/retailers/:slug/sales_channels"
          element={<ProtectedRoute element={SalesChannelsScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/brands"
          element={<ProtectedRoute element={BrandsScreenMC} />}
        />
        <Route exact path="/retailers/:slug/teams" element={<ProtectedRoute element={Teams} />} />
        <Route exact path="/retailers/:slug/roles" element={<ProtectedRoute element={Roles} />} />
        <Route
          exact
          path="/retailers/:slug/conversation_topics"
          element={<ProtectedRoute element={ConversationTopics} />}
        />
        <Route
          exact
          path="/retailers/:slug/business_rules"
          element={<ProtectedRoute element={MessagingRulesScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/locations"
          element={<ProtectedRoute element={LocationsScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/edit_business_info"
          element={<ProtectedRoute element={EditBusinessInfoScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/my_account"
          element={<ProtectedRoute element={MyAccountScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/domain"
          element={<ProtectedRoute element={DomainScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/schedules"
          element={<ProtectedRoute element={SchedulesScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/pricing"
          element={<ProtectedRoute element={PaymentPlansScreenMC} />}
        />
        <Route
          path="/retailers/:slug/consumption"
          element={<ProtectedRoute element={ConsumptionScreenMC} />}
        />
        <Route
          path="/retailers/:slug/billing_history"
          element={<ProtectedRoute element={BillingHistoryScreenMC} />}
        />
        <Route
          path="/retailers/:slug/invoice/:id"
          element={<ProtectedRoute element={InvoiceScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/contacts"
          element={<ProtectedRoute element={ContactsScreenMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/team_assignments/logs"
          element={<ProtectedRoute element={AssignmentLogs} />}
        />
        <Route
          exact
          path="/retailers/:slug/team_assignments"
          element={<ProtectedRoute element={TeamAssignments} />}
        />
        <Route
          exact
          path="/retailers/:slug/quick_answers"
          element={<ProtectedRoute element={QuickAnswers} />}
        />
        <Route
          exact
          path="/retailers/:slug/quick_answers/new"
          element={<ProtectedRoute element={ManageQuickAnswer} />}
        />
        <Route
          exact
          path="/retailers/:slug/quick_answers/:id/edit"
          element={<ProtectedRoute element={ManageQuickAnswer} />}
        />
        <Route
          exact
          path="/retailers/:slug/mia_conversations_attended"
          element={<ProtectedRoute element={MiaConversationsAttendedMC} />}
        />
        <Route
          exact
          path="/retailers/:slug/campaign_fee_consumptions"
          element={<ProtectedRoute element={CampaignFeeConsumptionsMC} />}
        />
        <Route path="/retailers/:slug/chatbot_ai/*" element={<ChatbotAIRoutes />} />
        <Route exact path="/retailers/:slug/plans" element={<ProtectedRoute element={Plans} />} />
        <Route exact path="/retailers/:slug/plans/:id" element={<ProtectedRoute element={PaymentPlanDetails} />} />
        <Route exact path="/retailers/:slug/import_customers" element={<ProtectedRoute element={ImportCustomers} />} />
        <Route exact path="/retailers/:slug/shopify-automations" element={<ProtectedRoute element={ShopifyAutomationsModuleMC} />} />
        <Route exact path="/retailers/:slug/shopify-automations/config" element={<ProtectedRoute element={ShopifyConfigModuleMC} />} />
        <Route exact path="/retailers/:slug/shopify-automations/whatsapp-widget" element={<ProtectedRoute element={ShopifyWhatsAppWidgetModuleMC} />} />
        <Route exact path="/retailers/:slug/shopify-automations/:webId/history" element={<ProtectedRoute element={ShopifyHistoryModuleMC} />} />
        <Route exact path="/retailers/:slug/growth_tools/automations" element={<ProtectedRoute element={GrowthToolAutomation} />} />
        <Route exact path="/retailers/:slug/growth_tools/automations/:id/history" element={<ProtectedRoute element={GrowthToolAutomationHistory} />} />
        <Route exact path="/retailers/:slug/growth_tools/automations/funnels/:id/history" element={<ProtectedRoute element={GrowthToolFunnelHistory} />} />
        <Route exact path="/retailers/:slug/growth_tools/automations/funnels/:id" element={<ProtectedRoute element={GrowthToolAutomationFunnels} />} />
        <Route exact path="/retailers/:slug/notification_settings" element={<ProtectedRoute element={Notifications} />} />
        <Route exact path="/retailers/:slug/business_config" element={<ProtectedRoute element={BusinessConfigScreenMC} />} />
        <Route exact path="/retailers/:slug/regional_configs" element={<ProtectedRoute element={RegionalConfigsScreenMC} />} />
        <Route exact path="/retailers/:slug/shipping" element={<ProtectedRoute element={ShippingScreenMC} />} />
        <Route path="/retailers/:slug/shipping_cost" element={<ProtectedRoute element={ShippingCostLayout} />}>
          <Route index element={<ProtectedRoute element={ShippingCost} />} />
          <Route path="fixed_cost" element={<ProtectedRoute element={FixedCost} />} />
          <Route path="costs_by_location" element={<ProtectedRoute element={CostsByLocation} />} />
        </Route>

        <Route exact path="/retailers/:slug/integrations/whatsapp" element={<ProtectedRoute element={WhatsAppBusinessMC} />} />
        <Route exact path="/retailers/:slug/integrations/whatsapp_business" element={<ProtectedRoute element={WhatsAppBusinessMC} />} />
        <Route exact path="/retailers/:slug/integrations/whatsapp_v2" element={<ProtectedRoute element={WhatsAppScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/woocommerce" element={<WoocommerceScreenMC />} />
        <Route exact path="/retailers/:slug/integrations/messenger_v2" element={<MessengerScreenMC />} />
        <Route exact path="/retailers/:slug/integrations/tiktok_messages" element={<ProtectedRoute element={TikTokMessagesScreenMC} />} />
        <Route exact path="/retailers/:slug/integrations/tiktok_comments" element={<ProtectedRoute element={TikTokCommentsScreenMC} />} />
        <Route exact path="/retailers/:slug/onboarding" element={<OnboardingApp />} />
        <Route exact path="/retailers/:slug/whatsapp_widget" element={<ProtectedRoute element={WhatsAppWidget} />} />
        <Route exact path="/retailers/:slug/campaigns" element={<ProtectedRoute element={CampaignsMC} requiredPermission="view_campaigns" />} />
        <Route exact path="/retailers/:slug/campaigns/:id/stats" element={<ProtectedRoute element={CampaignsStatusScreenMC} requiredPermission="view_campaigns" />} />
        <Route path="/retailers/:slug/calendar_events" element={<ProtectedRoute element={CalendarEventsScreenMC} />} />
        <Route path="/retailers/:slug/quotes/templates" element={<ProtectedRoute element={TemplatesListModuleMC} />} />
        <Route path="/retailers/:slug/quotes/templates/new" element={<ProtectedRoute element={TemplateEditorModuleMC} />} />
        <Route path="/retailers/:slug/quotes/templates/:id/edit" element={<ProtectedRoute element={TemplateEditorModuleMC} />} />
        <Route path="/retailers/:slug/quotes" element={<ProtectedRoute element={QuoteDraftsListModuleMC} />} />
        <Route path="/retailers/:slug/quotes/new" element={<ProtectedRoute element={QuoteEditorModuleMC} />} />
        <Route path="/retailers/:slug/quotes/:id/edit" element={<ProtectedRoute element={QuoteEditorModuleMC} />} />
        <Route path="/retailers/:slug/reminders" element={<ProtectedRoute element={RemindersScreenMC} />} />
        <Route path="/retailers/:slug/academy" element={<ProtectedRoute element={AcademyScreenMC} />} />
        <Route path="/retailers/:slug/test-error" element={<ProtectedRoute element={TestErrorScreenMC} superAdminOnly />} />
        {/* Super Admin */}
        <Route exact path="/superadmin19" element={<ProtectedRoute element={SuperAdminDashboardScreenMC} superAdminOnly />} />
        <Route exact path="/superadmin19/retailers" element={<ProtectedRoute element={SuperAdminRetailerListScreenMC} superAdminOnly />} />
        <Route exact path="/superadmin19/financial_data" element={<ProtectedRoute element={SuperAdminFinancialDataScreenMC} superAdminOnly />} />
        <Route exact path="/superadmin19/support" element={<ProtectedRoute element={SuperAdminSupportScreenMC} superAdminOnly />} />
        <Route path="/retailers/:slug/webhooks" element={<ProtectedRoute element={WebhooksScreenMC} />} />
        <Route path="/retailers/:slug/whatsapp_flows" element={<ProtectedRoute element={WhatsappFlowsScreenMC} />} />
        <Route path="/retailers/:slug/whatsapp_flows/:id/responses" element={<ProtectedRoute element={WhatsappFlowResponsesScreenMC} />} />
        <Route path="/retailers/:slug/ads_center" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/paywall_preview" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/demo/:view" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/campaigns" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/campaigns/:campaignId" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/campaigns/:campaignId/adsets" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/campaigns/:campaignId/adsets/:adsetId" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/campaigns/:campaignId/ads" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/campaigns/:campaignId/ads/:adId" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/funnel" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/attribution" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/conversion_api" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/statistics/channels" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/statistics/funnel" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/statistics" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/ads_center/integrations" element={<ProtectedRoute element={AdsCenterScreenMC} />} />
        <Route path="/retailers/:slug/marketing/*" element={<ProtectedRoute element={MarketingScreenMC} requiredFeatureFlag="marketing_iq_enabled" />} />
        <Route
          path={CRM_ROUTES.TAGS.path}
          element={<TagMC />}
        />
        <Route
          path="/retailers/:slug/history_calls"
          element={<HistoryCallScreenMC />}
        />
        <Route
          path="/retailers/:slug/calls/integration"
          element={<CallsIntegrationScreenMC />}
        />
        <Route
          path="/retailers/:slug/live_calls"
          element={<VoiceLiveCallsScreenMC />}
        />
        <Route exact path="/get_your_plan/:token" element={<GetYourPlanScreen />} />
        {/* Pantallas de error */}
        <Route path="/404" element={<ErrorPageScreenMC errorType="not_found" />} />
        <Route path="/500" element={<ErrorPageScreenMC errorType="internal_error" />} />
      </Routes>
      <ProductSwitcherMC />
      <MarketingSidebarSyncMC />
      <TikTokNavBadgeSyncMC />
      <WhatsappCallsNavSyncMC />
      <ModuleTitleMC />
      <SidebarContainerMC />
    </ErrorBoundary>
  </HistoryRouter>
);

export default AppRoutes;
